import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { useTheme } from '../../contexts/ThemeContext';
import DojoMark from '../common/DojoMark';
import { PrimaryButton, Field, useInputClass } from '../dojo';

/**
 * Sign in.
 *
 * Rebuilt to match the mobile screen rather than restyled: the same mark at the
 * same relative size, the same "Welcome Back!" and "Sign in to continue to your
 * tasks", the same labelled Email and Password fields inside a single card, the
 * same Sign In button, and the same line beneath about not having an account.
 * The error strings are copied verbatim from the phone -- someone who mistypes
 * a password on both should be told the same thing in the same words.
 *
 * What differs is only what must. A phone screen is one column at 390pt; a
 * browser is not, so the card is centred with a bounded width and the type
 * steps up at sm. The proportions and the geometry -- 2pt borders, the two
 * shaded faces on the button, the radii -- are the phone's, and come from the
 * shared dojo kit so they cannot drift screen to screen.
 */
const LoginScreen: React.FC = () => {
  const { colors, isDark } = useTheme();
  const input = useInputClass();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      // Verbatim from the mobile screen, so both platforms say the same thing.
      const code = err?.code || '';
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
        setError('Incorrect email or password. Please try again.');
      } else if (code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!email) {
      setError('Enter your email address first, then choose Forgot password.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
      setError('');
    } catch {
      setError('Could not send a reset email. Check the address and try again.');
    }
  };

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text} flex items-center justify-center px-5 py-10`}>
      <div className="w-full max-w-[440px]">
        <div className="flex flex-col items-center text-center mb-8">
          {/* Drawn, not loaded: the same geometry as the app icon and the
              phone's sign-in screen, so the three cannot drift apart. The
              keyline only appears on the dark ground, where the mark's own
              face colour matches the background. */}
          <div className="mb-7">
            <DojoMark
              size={112}
              title="taskDojo"
              outlineColor={isDark ? 'rgba(250, 247, 240, 0.22)' : undefined}
            />
          </div>
          <h1 className="text-[34px] font-black tracking-[-1px] leading-none">Welcome Back!</h1>
          <p className={`mt-2 text-base font-medium ${colors.textSecondary}`}>
            Sign in to continue to your tasks
          </p>
        </div>

        {/* One card holding the whole form, as on the phone. */}
        <form
          onSubmit={handleLogin}
          className={`${colors.card} border-2 ${colors.cardBorder} rounded-[22px] p-6 sm:p-7`}
        >
          <Field label="Email" className="mb-5">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              autoCapitalize="none"
              autoComplete="email"
              className={input}
            />
          </Field>

          <Field label="Password" className="mb-6">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoCapitalize="none"
                autoComplete="current-password"
                className={`${input} pr-12`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 ${colors.textSecondary}
                  hover:${colors.text} transition-colors`}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                      strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M1 1l22 22" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </Field>

          {error && (
            <div
              role="alert"
              className="mb-5 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10 px-4 py-3
                text-sm font-semibold text-dojo-red"
            >
              {error}
            </div>
          )}
          {resetSent && !error && (
            <div
              role="status"
              className="mb-5 rounded-[14px] border-2 border-dojo-green/50 bg-dojo-green/10 px-4 py-3
                text-sm font-semibold text-dojo-greenDark"
            >
              Reset email sent. Check your inbox.
            </div>
          )}

          <PrimaryButton type="submit" disabled={loading} className="w-full">
            {loading ? 'Signing in…' : 'Sign In'}
          </PrimaryButton>

          <button
            type="button"
            onClick={handleReset}
            className={`w-full mt-4 text-sm font-bold ${colors.textSecondary} hover:${colors.text} transition-colors`}
          >
            Forgot password?
          </button>
        </form>

        <p className={`mt-6 text-center text-sm ${colors.textSecondary}`}>
          Don't have an account?{' '}
          <span className="font-bold text-dojo-red">Create one in the taskDojo app</span>
        </p>
      </div>
    </div>
  );
};

export default LoginScreen;
